import React, { useState } from 'react';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, CircularProgress } from '@mui/material';
import ModelComparisonChart from './ModelComparisonChart';

const ModelRankingTable = ({ selectedMarker, Rate, option, startDate, endDate, imp_or_anom }) => {
    const [metricsData, setMetricsData] = useState([]);
    const [modelsData, setModelsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [orderBy, setOrderBy] = useState('RMSE');
    const [order, setOrder] = useState('asc');
    const [showTable, setShowTable] = useState(false);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleShowRanking = async () => {
        if (!selectedMarker) {
            alert("Please select a well marker first");
            return;
        }

        setIsLoading(true);
        setShowTable(true);

        let url = '';

        // Check if both startDate and endDate are provided
        if (startDate && endDate) {
            // Validate date range
            if (startDate > endDate) {
                alert("Start date cannot be after end date.");
                setIsLoading(false);
                return;
            }

            // The HTML date input already provides dates in YYYY-MM-DD format
            const formattedStartDate = startDate;
            const formattedEndDate = endDate;

            // API call with date range
            url = `https://warpo-dashboard.cse.buet.ac.bd/api/send-datalogs-for-ranking/${selectedMarker.wellId}/${Rate}/${option}/${formattedStartDate}/${formattedEndDate}`;
        } else {
            // Fallback to API without date range
            url = `https://warpo-dashboard.cse.buet.ac.bd/api/send-datalogs-for-ranking/${selectedMarker.wellId}/${Rate}/${option}/null/null`;
        }

        try {
            const res = await fetch(url, {
                method: "POST", 
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem('token')}`,
                    "Content-Type": "application/json"  // Add this to specify JSON content
                },
                body: JSON.stringify({  // Move body outside of headers
                    imp_or_anom: imp_or_anom
                })
 });
            if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

            const data = await res.json();
            console.log("Received ranking data:", data);

            // Set the metrics and models data from the response
            if (data && data.metrics) {
                setMetricsData(data.metrics);
            } else {
                console.error("No metrics data found in response");
            }

            if (data && data.models_data) {
                setModelsData(data.models_data);
            } else {
                console.error("No models data found in response");
            }
        } catch (err) {
            console.error("Error fetching ranking data:", err);
            alert("Failed to fetch model ranking data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Function to sort the data
    const sortedData = React.useMemo(() => {
        if (!metricsData || metricsData.length === 0) return [];

        return [...metricsData].sort((a, b) => {
            const valueA = a[orderBy];
            const valueB = b[orderBy];

            if (valueA < valueB) {
                return order === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [metricsData, order, orderBy]);

    return (
        <div style={{ marginTop: '0px', marginBottom: '-20px' }}>
            <Button
                variant="contained"
                onClick={handleShowRanking}
                className="button ranking-button"
                style={{
                    height: "40px",
                    width: "200px",
                    textTransform: "none",
                    fontSize: "16px",
                    marginBottom: "20px"
                }}
            >
                Rank Models
            </Button>

            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <CircularProgress />
                </div>
            )}

            {showTable && !isLoading && (
                <>
                    <TableContainer component={Paper}>
                        <Table aria-label="model ranking table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Model</TableCell>
                                    {['RMSE', 'MSE', 'MAE', 'MAPE'].map((metric) => (
                                        <TableCell key={metric} align="right">
                                            <TableSortLabel
                                                active={orderBy === metric}
                                                direction={orderBy === metric ? order : 'asc'}
                                                onClick={() => handleRequestSort(metric)}
                                                style={{
                                                    fontWeight: 'bold',
                                                    color: orderBy === metric ? '#1976d2' : 'inherit'
                                                }}
                                            >
                                                {metric}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedData.map((row) => (
                                    <TableRow key={row.Model}>
                                        <TableCell component="th" scope="row">
                                            {row.Model}
                                        </TableCell>
                                        <TableCell align="right" style={{ color: orderBy === 'RMSE' ? '#1976d2' : 'inherit' }}>
                                            {typeof row.RMSE === 'number' ? row.RMSE.toFixed(4) : row.RMSE}
                                        </TableCell>
                                        <TableCell align="right" style={{ color: orderBy === 'MSE' ? '#1976d2' : 'inherit' }}>
                                            {typeof row.MSE === 'number' ? row.MSE.toFixed(4) : row.MSE}
                                        </TableCell>
                                        <TableCell align="right" style={{ color: orderBy === 'MAE' ? '#1976d2' : 'inherit' }}>
                                            {typeof row.MAE === 'number' ? row.MAE.toFixed(4) : row.MAE}
                                        </TableCell>
                                        <TableCell align="right" style={{ color: orderBy === 'MAPE' ? '#1976d2' : 'inherit' }}>
                                            {typeof row.MAPE === 'number' ? row.MAPE.toFixed(4) : row.MAPE}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Add the model comparison chart */}
                    {modelsData.length > 0 && (
                        <ModelComparisonChart modelsData={modelsData} />
                    )}
                </>
            )}
        </div>
    );
};

export default ModelRankingTable;